// Students API utility functions

import { API_BASE_URL, getAuthHeaders } from './apiConfig';

const STUDENTS_API = `${API_BASE_URL}/students`;

// Fetch all students
export async function fetchStudents() {
  const response = await fetch(STUDENTS_API, { headers: { ...getAuthHeaders() } });
  return response.json();
}

export async function getStudentEmployment(studentId) {
  const response = await fetch(`${STUDENTS_API}/${studentId}/employment`, {
    headers: { ...getAuthHeaders() }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch student employment information');
  }
  return data;
}

// Update student status
export async function updateStudentStatus(studentId, status) {
  const response = await fetch(`${STUDENTS_API}/${studentId}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ status })
  });
  return response.json();
}

export async function bulkUpdateStudentStatus(studentIds, status) {
  const response = await fetch(`${STUDENTS_API}/bulk-status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ studentIds, status })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Failed to update student status');
  }
  return data;
}

// Add new student
export async function addStudent(formData) {
  const response = await fetch(STUDENTS_API, {
    method: 'POST',
    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    headers: { ...getAuthHeaders() },
    body: formData
  });
  return response.json();
}

// Assign course to student
export async function assignCourseToStudent(data) {
  const response = await fetch(`${STUDENTS_API}/assign-course`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(data)
  });
  return response.json();
}

// Get student by ID
export async function getStudentById(studentId) {
  const response = await fetch(`${STUDENTS_API}/${studentId}`, { headers: { ...getAuthHeaders() } });
  return response.json();
}

// Get comprehensive student details
export async function getStudentDetails(studentId) {
  const response = await fetch(`${STUDENTS_API}/${studentId}/details`, { headers: { ...getAuthHeaders() } });
  return response.json();
}

// Get student data for editing
export async function getStudentForEdit(studentId) {
  const response = await fetch(`${STUDENTS_API}/${studentId}/edit`, { headers: { ...getAuthHeaders() } });
  return response.json();
}

// Delete student
export async function deleteStudent(studentId) {
  const response = await fetch(`${STUDENTS_API}/${studentId}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() }
  });
  return response.json();
}

export async function updateStudent(studentId, studentData) {
  const response = await fetch(`${STUDENTS_API}/${studentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(studentData)
  });
  return response.json();
}

export async function assignCareerCoachToStudent(studentId, careerCoachId) {
  const response = await fetch(`${STUDENTS_API}/${studentId}/career-coach`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ careerCoachId: careerCoachId ?? null })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Failed to assign career coach');
  }
  return data;
}

function appendParticularFilesToFormData(formData, files) {
  const list = Array.isArray(files) ? files : [files];
  list.forEach((f) => {
    if (f) formData.append('files', f);
  });
}

// Upload one or more particular files for a student (appends to existing)
export const uploadParticular = async (studentId, particularName, files) => {
  try {
    const formData = new FormData();
    appendParticularFilesToFormData(formData, files);

    const response = await fetch(`${API_BASE_URL}/students/${studentId}/particulars?particularName=${encodeURIComponent(particularName)}`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload particular');
    }

    return data;
  } catch (error) {
    console.error('Error uploading particular:', error);
    throw error;
  }
};

// Upload a document file for a student (SSN, DL, Medical Evaluation Form, etc.)
export const uploadStudentDocument = async (studentId, documentType, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    // Update the student's document fields directly in the students table
    // documentType should be either 'social_security_upload', 'dl_upload', or 'medical_evaluation_form'
    const response = await fetch(`${API_BASE_URL}/students/${studentId}/update-documents`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload document');
    }

    return data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

// Add more files to a particular (appends)
export const updateParticular = async (studentId, particularName, files) => {
  try {
    const formData = new FormData();
    appendParticularFilesToFormData(formData, files);

    const response = await fetch(`${API_BASE_URL}/students/${studentId}/particulars?particularName=${encodeURIComponent(particularName)}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders() },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update particular');
    }

    return data;
  } catch (error) {
    console.error('Error updating particular:', error);
    throw error;
  }
};

// Delete all files for a particular
export const deleteParticular = async (studentId, particularName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/students/${studentId}/particulars?particularName=${encodeURIComponent(particularName)}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete particular');
    }

    return data;
  } catch (error) {
    console.error('Error deleting particular:', error);
    throw error;
  }
};

// Remove a single file from a particular
export const deleteParticularFile = async (studentId, particularName, filePath) => {
  try {
    const q = new URLSearchParams({
      particularName,
      filePath,
    });
    const response = await fetch(`${API_BASE_URL}/students/${studentId}/particulars?${q.toString()}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to remove file');
    }

    return data;
  } catch (error) {
    console.error('Error removing particular file:', error);
    throw error;
  }
};

// Get students by course and session
export const getStudentsByCourseSession = async (courseId, sessionId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/students/by-course-session?courseId=${courseId}&sessionId=${sessionId}`,
      { headers: { ...getAuthHeaders() } }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch students');
    }
    return data;
  } catch (error) {
    console.error('Error fetching students by course/session:', error);
    throw error;
  }
};

// Transfer multiple students to new course/session
export const transferStudents = async (studentIds, courseId, sessionId, reason = '', notes = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/students/transfer-students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ studentIds, courseId, sessionId, reason, notes }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to transfer students');
    }
    return data;
  } catch (error) {
    console.error('Error transferring students:', error);
    throw error;
  }
};

// Fetch all transfer history with optional filters
export const fetchTransferHistory = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.studentSearch) queryParams.append('studentSearch', filters.studentSearch);
    if (filters.courseId) queryParams.append('courseId', filters.courseId);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);
    
    const response = await fetch(
      `${API_BASE_URL}/students/transfer-history?${queryParams.toString()}`,
      { headers: { ...getAuthHeaders() } }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch transfer history');
    }
    return data;
  } catch (error) {
    console.error('Error fetching transfer history:', error);
    throw error;
  }
};

// Fetch transfer history for a specific student
export const fetchStudentTransferHistory = async (studentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/students/${studentId}/transfer-history`, {
      headers: { ...getAuthHeaders() },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch student transfer history');
    }
    return data;
  } catch (error) {
    console.error('Error fetching student transfer history:', error);
    throw error;
  }
}; 